import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import ExcelImportDropzone from '../../components/ui/ExcelImportDropzone';

const VendorExcelImport = () => {
  const navigate = useNavigate();
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);
  const [cleanImportMode, setCleanImportMode] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleImportComplete = (result) => {
    setImportResult(result);
    setError(null);
    console.log('Import completed:', result);
  };

  const handleImportError = (errorMessage) => {
    setError(errorMessage);
    setImportResult(null);
  };

  const handleBackToVendors = () => {
    navigate('/vendor-master-list');
  };

  const handleToggleCleanImport = (e) => {
    const isChecked = e.target.checked;
    if (isChecked) {
      // Show confirmation dialog
      setShowConfirmDialog(true);
    } else {
      setCleanImportMode(false);
    }
  };

  const handleConfirmCleanImport = () => {
    setCleanImportMode(true);
    setShowConfirmDialog(false);
  };

  const handleCancelCleanImport = () => {
    setCleanImportMode(false);
    setShowConfirmDialog(false);
  };

  const breadcrumbItems = [
    { label: 'Dashboard', href: '/dashboard-overview' },
    { label: 'Vendor Management', href: '/vendor-master-list' },
    { label: 'Excel Import' }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      <Breadcrumb items={breadcrumbItems} />
      
      {/* Page Header */}
      <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-foreground mb-2">
                    Import Vendors from Excel
                  </h1>
                  <p className="text-text-secondary">
                    Upload an Excel file to bulk import vendor data into the system
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={handleBackToVendors}
                  className="flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  <span>Back to Vendors</span>
                </Button>
              </div>
      </div>

      {/* Error Display */}
      {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="text-red-800 font-medium">Import Error</span>
                </div>
                <p className="text-red-700 mt-1">{error}</p>
              </div>
      )}

      {/* Import Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-blue-900 mb-4">
                📋 Import Instructions
              </h3>
              <div className="space-y-3 text-blue-800">
                <div className="flex items-start space-x-2">
                  <span className="font-medium">1.</span>
                  <span>Download the Excel template to see the required column format</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="font-medium">2.</span>
                  <span>Fill in your vendor data following the template structure</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="font-medium">3.</span>
                  <span>Drag and drop your Excel file or click to select it</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="font-medium">4.</span>
                  <span>Review the import results and any warnings or errors</span>
                </div>
              </div>
      </div>

      {/* Import Options - Clean Import Mode Toggle */}
      <div className={`border rounded-lg p-6 mb-8 ${
              cleanImportMode 
                ? 'bg-orange-50 border-orange-300' 
                : 'bg-gray-50 border-gray-200'
            }`}>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Import Options
              </h3>
              
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="cleanImportMode"
                  checked={cleanImportMode}
                  onChange={handleToggleCleanImport}
                  className="mt-1 h-5 w-5 text-orange-600 focus:ring-orange-500 border-gray-300 rounded cursor-pointer"
                />
                <div className="flex-1">
                  <label htmlFor="cleanImportMode" className="font-medium text-gray-900 cursor-pointer">
                    Full Clean Import Mode
                  </label>
                  
                  {cleanImportMode && (
                    <div className="mt-3 space-y-2">
                      <div className="flex items-start space-x-2 text-orange-800 bg-orange-100 p-3 rounded-lg">
                        <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div className="flex-1">
                          <p className="font-semibold">WARNING: This mode will:</p>
                          <ul className="mt-2 space-y-1 text-sm">
                            <li>• DELETE ALL existing vendor data</li>
                            <li>• Make all fields optional (email, phone, company name)</li>
                            <li>• Import entire Excel without validation</li>
                          </ul>
                        </div>
                      </div>
                      <p className="text-sm text-orange-700 font-medium">
                        Use ONLY for complete data replacement!
                      </p>
                    </div>
                  )}
                  
                  {!cleanImportMode && (
                    <p className="text-sm text-gray-600 mt-1">
                      When disabled, normal validation applies (required fields, duplicate checks)
                    </p>
                  )}
                </div>
              </div>
      </div>

      {/* Drag and Drop Component */}
      <ExcelImportDropzone
              onImportComplete={handleImportComplete}
              onError={handleImportError}
              cleanImport={cleanImportMode}
        className="mb-8"
      />

      {/* Success Actions */}
      {importResult && importResult.summary?.successful_imports > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-green-900 mb-2">
                      ✅ Import Successful!
                    </h3>
                    <p className="text-green-800">
                      Successfully imported {importResult.summary.successful_imports} vendor(s)
                    </p>
                  </div>
                  <div className="flex space-x-3">
                    <Button
                      variant="outline"
                      onClick={() => window.location.reload()}
                    >
                      Import More
                    </Button>
                    <Button
                      onClick={handleBackToVendors}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      View Vendors
                    </Button>
                  </div>
                </div>
              </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="ml-3 text-lg font-semibold text-gray-900">
                Confirm Full Clean Import
              </h3>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-3">
                This will <span className="font-bold text-red-600">DELETE ALL existing vendor records</span> and import fresh data from the Excel file.
              </p>
              <p className="text-gray-700 font-semibold">
                This action cannot be undone!
              </p>
              <p className="text-gray-600 text-sm mt-3">
                Are you sure you want to proceed?
              </p>
            </div>
            
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={handleCancelCleanImport}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmCleanImport}
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
              >
                Yes, Delete & Import
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorExcelImport;

